/* eslint-disable default-case */
/* eslint-disable no-restricted-syntax */
import { ApolloClient, from } from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import { ApolloLink, fromPromise, NextLink, Operation } from 'apollo-link';
import { onError } from 'apollo-link-error';

import { nanoid } from 'nanoid';
import * as Sentry from '@sentry/react';
import i18n from 'i18next';

import { authStateVar, appState } from 'core/state';
import { cache } from 'core/cache';
import { RefreshMutation } from 'core/operations';
import { authService } from 'services/AuthorizationService';
import { GlobalToastTypeNames, NetworkErrors, ToastTypes } from 'types';

/**
 * Terminating link. `createUploadLink` is a drop-in replacement for
 * `createHttpLink`: it behaves identically for ordinary operations and only
 * switches to a `multipart/form-data` body (per the GraphQL multipart request
 * spec) when the variables contain a `File`/`Blob` — which is what lets the
 * diploma upload mutations send bytes. The JWT (`middlewareLink`) and
 * token-refresh (`errorLink`) links in front of it are unaffected.
 */
const httpLink = createUploadLink({
  uri: process.env.REACT_APP_GRAPH_URI,
});

/**
 * ТЗ 0122 («ИИ в почте»): язык интерфейса — серверу (суть письма, вопросы,
 * причины разбора приходят на нём): `X-ERP-Language` и запасной
 * `Accept-Language`. 🔴 `x-erp-language` обязан быть в `CORS_ALLOW_HEADERS`
 * бэкенда (ТЗ 0122): без него предварительный запрос с другого домена
 * (erp → erp-api) отклоняется и не уходит НИ ОДИН запрос — при выкате
 * бэкенд раньше фронта.
 */
const uiLanguageHeader = (): Record<string, string> => {
  const lang = (i18n.language || '').slice(0, 2).toLowerCase();
  return lang === 'ru' || lang === 'pl'
    ? { 'Accept-Language': lang, 'X-ERP-Language': lang }
    : {};
};

const middlewareLink = new ApolloLink((operation, forward) => {
  const { access_token } = authStateVar();
  const oldHeaders = operation.getContext().headers;
  operation.setContext({
    headers: {
      ...oldHeaders,
      ...uiLanguageHeader(),
      ...(access_token !== '' ? { Authorization: `JWT ${access_token}` } : {}),
    },
  });
  return forward(operation);
});

/**
 * Человекочитаемый текст сетевой ошибки.
 *
 * У ошибки валидации GraphQL (HTTP 400) полезное лежит НЕ в `message` — там
 * всегда общее «Response not successful: Received status code 400», — а в теле
 * ответа: `result.errors[].message`. Без этого невозможно понять, какая именно
 * переменная не сошлась по типу.
 */
interface IServerErrorLike {
  statusCode: number;
  message: string;
  result?: { errors?: { message?: string }[] };
}

const describeNetworkError = (networkError: IServerErrorLike): string => {
  const bodyErrors = networkError.result?.errors;
  const detail = Array.isArray(bodyErrors)
    ? bodyErrors
        .map((e) => e?.message)
        .filter(Boolean)
        .join('; ')
    : '';

  return `${networkError.statusCode}: ${detail || networkError.message}`;
};

/**
 * То же, но для показа ЧЕЛОВЕКУ.
 *
 * `result.errors[]` от graphql-core печатает значение переменной, на которой
 * споткнулась валидация, — в проде это лишняя поверхность раскрытия (в списке
 * операций есть и `login($password: String!)`). Разработчику подробность нужна,
 * пользователю — нет; в Sentry полный текст уходит в любом случае.
 */
const userFacingNetworkError = (networkError: IServerErrorLike): string =>
  process.env.NODE_ENV === 'production'
    ? `${networkError.statusCode}: ${networkError.message}`
    : describeNetworkError(networkError);

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward, response }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        const { message } = err;

        const firtstPath = err.path && err.path[0];

        const accessTokenExpired = err.message === 'Error decoding signature';
        if (accessTokenExpired) {
          return refreshTokens(operation, forward);
        }

        const refreshTokenExpired =
          firtstPath === 'refreshToken' && message.includes('token is expired');

        if (refreshTokenExpired) {
          logout();
        }
      }
    }
    if (networkError) {
      if ('result' in networkError) {
        const { statusCode } = networkError;
        const currentState = appState();

        const serverNotAvaliable = statusCode === 500;
        if (serverNotAvaliable) {
          appState({
            ...currentState,
            networkError: NetworkErrors.internalServerError,
          });

          return;
        }

        const serviceUnavailable = statusCode === 503;
        if (serviceUnavailable) {
          appState({
            ...currentState,
            networkError: NetworkErrors.serviceUnavailable,
          });

          return;
        }

        const accessTokenExpired = statusCode === 403;
        // TODO: add checking for token expired, statusCode is not enough probably

        if (accessTokenExpired) {
          return refreshTokens(operation, forward);
        }

        appState({
          ...currentState,
          toasts: [
            ...(currentState.toasts || []),
            {
              id: nanoid(6),
              data: {
                // Перепроверка U2: заголовок на языке интерфейса (был
                // зашит по-английски). Глобальный экземпляр i18next — его
                // инициализирует src/i18n.ts до первого запроса.
                title: i18n.t('error.networkToastTitle'),
                text: userFacingNetworkError(networkError),
                type: ToastTypes.error,
              },
              type: GlobalToastTypeNames.NetworkError,
            },
          ],
        });

        // Такие ошибки не доходят до резолвера, поэтому бэкендовый Sentry их
        // не видит — сообщаем с фронта, иначе сбой остаётся невидимым совсем.
        // Группируем по операции и коду, а НЕ по тексту: graphql-core
        // подставляет в сообщение само значение переменной, поэтому без
        // fingerprint выходило по отдельному issue на каждую строку журнала —
        // тот же взрыв кардинальности, который и должен был подсветить баг.
        Sentry.captureMessage(
          `GraphQL ${statusCode} on ${operation.operationName}`,
          {
            level: Sentry.Severity.Error,
            fingerprint: [
              'graphql-network',
              String(statusCode),
              operation.operationName,
            ],
            extra: { detail: describeNetworkError(networkError) },
          },
        );

        // ВАЖНО: выйти. Раньше выполнение проваливалось в блок ниже, и ЛЮБАЯ
        // ошибка с кодом — включая 400 — дополнительно уводила на страницу
        // «500». Так контрактная ошибка выглядела как падение сервера: клик по
        // галочке в журнале слал строку в `Int!`, graphene 3 отвечал 400 ещё на
        // разборе переменных, а пользователь видел «500».
        return;
      }

      // Сюда попадаем только когда HTTP-статуса нет вовсе: обрыв сети, DNS,
      // CORS, отменённый запрос. Вот это действительно «сервер недоступен».
      if (!response || !response.data) {
        const currentState = appState();
        appState({
          ...currentState,
          networkError: NetworkErrors.internalServerError,
        });
      }
    }
  },
);

export const client = new ApolloClient({
  link: from([errorLink, middlewareLink, httpLink as any]),
  cache,
  // The devtools bridge keeps extra cache references alive — dev only.
  connectToDevTools: process.env.NODE_ENV !== 'production',
});

const getNewToken = () =>
  client
    .mutate({
      mutation: RefreshMutation,
      variables: {
        refresh_token: authService.getRefreshToken(),
        scope: process.env.REACT_APP_SCOPE || 'ERP',
      },
    })
    .then((response) => {
      const {
        token: access_token,
        refreshToken: refresh_token,
      } = response.data.refreshToken;
      authStateVar({ ...authStateVar(), access_token });
      authService.saveTokens({ access_token, refresh_token });
      return { access_token, refresh_token };
    });

const refreshTokens = (operation: Operation, forward: NextLink) => {
  authStateVar({ ...authStateVar(), access_token: '' });
  return fromPromise(
    getNewToken()
      .then(({ access_token }) => {
        if (access_token) {
          return access_token;
        }

        return logout();
      })
      .catch(() => {
        logout();
      }),
  )
    .filter((value) => Boolean(value))
    .flatMap((access_token) => {
      const oldHeaders = operation.getContext().headers;
      operation.setContext({
        headers: {
          ...oldHeaders,
          Authorization: `JWT ${access_token}`,
        },
      });

      return forward(operation);
    });
};

const logout = () => {
  authStateVar({ ...authStateVar(), access_token: '', isLoggedIn: false });
  authService.logOut();
  appState({ ...appState(), bootstrapped: false });
};
